import { cn } from "@core/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ComponentProps } from "react";
import type { IconType } from "react-icons";
import { Spinner } from "../Spinner";

// eslint-disable-next-line react-refresh/only-export-components
export const buttonStyles = cva(
  [
    "cursor-pointer",
    "flex",
    "items-center",
    "justify-center",
    "gap-2",
    "rounded-lg",
    "text-sm",
    "whitespace-nowrap",
    "overflow-hidden",
    "transition-all",
    "duration-200",
    // Remove default browser outline — we handle focus ourselves
    "outline-none",
    // Keyboard focus ring base — color overridden per variant
    "focus-visible:ring-2",
    "focus-visible:ring-offset-2",
  ],
  {
    variants: {
      variant: {
        solid: [
          "bg-primary-800 text-neutral-50 font-medium border-[1.5px] border-primary-600",
          "hover:bg-primary-900 hover:border-neutral-200",
          "focus-visible:border-accent-blue-800 focus-visible:ring-0",
          "disabled:bg-neutral-100 disabled:border-neutral-200 disabled:cursor-not-allowed disabled:scale-100 disabled:text-neutral-300",
        ],
        secondary: [
          "bg-white border-[1px] font-medium border-neutral-200 text-secondary-900",
          "hover:bg-neutral-50 hover:border-primary-800 hover:text-primary-800",
          "focus-visible:border-accent-blue-800 focus-visible:ring-0",
          "disabled:bg-neutral-100 disabled:border-neutral-200 disabled:cursor-not-allowed disabled:scale-100 disabled:text-neutral-300",
        ],
        ghost: [
          "bg-white border-[1px] font-medium border-neutral-200 text-neutral-400",
          "hover:bg-neutral-50 hover:text-neutral-500",
          "focus-visible:border-accent-blue-800 focus-visible:ring-0",
          "disabled:bg-neutral-200 disabled:cursor-not-allowed disabled:scale-100",
        ],
        text: [
          "bg-transparent text-secondary-900 font-medium",
          "hover:bg-neutral-100 hover:border-primary-800 hover:text-primary-800",
          "focus-visible:border-accent-blue-800 focus:border-[1px] focus-visible:ring-0",
          "disabled:bg-white disabled:border-neutral-200 disabled:cursor-not-allowed disabled:scale-100 disabled:text-neutral-300",
        ],
        link: [
          "bg-transparent text-secondary-900 border-none underline",
          "hover:bg-neutral-100 hover:border-primary-800 hover:text-primary-800",
          "focus-visible:border-accent-blue-800 focus:border-[1px] focus-visible:ring-0",
          "disabled:bg-transparent disabled:cursor-not-allowed disabled:text-secondary-300 disabled:scale-100",
        ],
      },
      size: {
        lg: "h-[3.1875rem] py-1 px-3",
        md: "h-[2.5625rem] py-1 px-3",
        sm: "h-[2rem] py-1 px-3",
      },
      iconOnly: {
        true: "p-0",
        false: "",
      },
      hasText: {
        true: "",
        false: "",
      },
      isLoading: {
        true: "cursor-not-allowed",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "solid",
        isLoading: true,
        className:
          "bg-primary-800 border-[1px] border-neutral-200 text-neutral-100",
      },
      {
        variant: "secondary",
        isLoading: true,
        className:
          "bg-neutral-50 border-[1px] border-neutral-200 text-primary-800",
      },
      {
        variant: "ghost",
        isLoading: true,
        className:
          "bg-transparent border-[1px] border-secondary-200 text-secondary-300",
      },
      {
        variant: "text",
        isLoading: true,
        className: "bg-white text-primary-800",
      },
      {
        variant: "link",
        isLoading: true,
        className: "bg-white text-primary-800",
      },
    ],
    defaultVariants: {
      variant: "solid",
      size: "md",
      iconOnly: false,
      hasText: true,
      isLoading: false,
    },
  },
);

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonStyles> & {
    leftIcon?: IconType | string;
    rightIcon?: IconType | string;
    iconOnly?: boolean;
    width?: string;
    height?: string;
    truncate?: boolean;
    isLoading?: boolean;
    iconStrokeWidth?: number;
    menu?: boolean;
  };

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      className,
      size,
      children,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      iconOnly = false,
      truncate = true,
      isLoading = false,
      iconStrokeWidth = 0.7,
      menu = false,
      disabled,
      ...props
    },
    ref,
  ) => {
    const isIconOnly = iconOnly || (!children && (!!LeftIcon || !!RightIcon));
    const hasText = !!children;
    const spinnerSize = size === "lg" ? "lg" : size === "sm" ? "sm" : "md";

    return (
      <button
        ref={ref}
        className={cn(
          buttonStyles({
            variant,
            size,
            iconOnly: isIconOnly,
            hasText,
            isLoading,
          }),
          isIconOnly && "w-10",
          className,
        )}
        disabled={disabled}
        {...props}
      >
        {menu ? (
          // ================= MENU LAYOUT =================
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              {LeftIcon && (
                <LeftIcon
                  className="flex-shrink-0 text-2xl"
                  strokeWidth={iconStrokeWidth}
                />
              )}
              {children && (
                <span
                  className={cn("overflow-hidden", truncate && "text-ellipsis")}
                >
                  {children}
                </span>
              )}
            </div>
            {RightIcon && (
              <RightIcon
                className="flex-shrink-0 text-2xl"
                strokeWidth={iconStrokeWidth}
              />
            )}
          </div>
        ) : (
          // ================= NORMAL LAYOUT =================
          <>
            {!isLoading && LeftIcon && (
              <LeftIcon
                className="flex-shrink-0 text-2xl"
                strokeWidth={iconStrokeWidth}
              />
            )}

            {isLoading ? (
              <Spinner variant={variant ?? undefined} size={spinnerSize} />
            ) : (
              children && (
                <span
                  className={cn("overflow-hidden", truncate && "text-ellipsis")}
                >
                  {children}
                </span>
              )
            )}

            {!isLoading && RightIcon && (
              <RightIcon
                className="flex-shrink-0 text-2xl"
                strokeWidth={iconStrokeWidth}
              />
            )}
          </>
        )}
      </button>
    );
  },
);

export default Button;
